import { useCallback, useMemo } from "react";
import { useIssueFormatter } from "../../../application/hooks/useIssueFormatter";
import { ValidationIssue } from "../../../domain/entities/ValidationIssue";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";

/** What the dialog shows: a list of warnings, or one pre-formatted sentence. */
export interface SaveWarningsContent {
  /** Translated; without it the dialog keeps its default title ("Accommodation saved"). */
  readonly title?: string;
  /** Translated sentence shown instead of the intro, above the list. */
  readonly summary?: string;
  /** Translated, in the order received; shown as a bullet list. */
  readonly messages: readonly string[];
}

export interface SaveWarningsDialogProps extends SaveWarningsContent {
  readonly show: boolean;
  readonly onAccept: () => void;
}

export interface SaveWarningsSummary {
  readonly title: string;
  readonly summary: string;
  /** Shown as a bullet list under the summary. */
  readonly items?: readonly string[];
}

const EMPTY_CONTENT: SaveWarningsContent = { messages: [] };

/**
 * One summary of the warnings of a successful save. `showWarnings` (one line per warning) and
 * `showSummary` (one pre-formatted sentence, optionally followed by a list) resolve when the user accepts; `showWarnings`
 * resolves at once when there is nothing to show. Render `SaveWarningsDialog` with `dialog`.
 */
export function useSaveWarningsDialog() {
  const issueFormatter = useIssueFormatter();
  const { open, dialog: control } = useConfirmDialog<SaveWarningsContent>();

  const showWarnings = useCallback(
    async (warnings: readonly ValidationIssue[]): Promise<void> => {
      if (warnings.length === 0) {
        return;
      }
      await open({ messages: warnings.map(warning => issueFormatter.format(warning)) });
    },
    [issueFormatter, open]
  );

  const showSummary = useCallback(
    async ({ title, summary, items = [] }: SaveWarningsSummary): Promise<void> => {
      await open({ title, summary, messages: items });
    },
    [open]
  );

  const dialog = useMemo<SaveWarningsDialogProps>(
    () => ({
      ...(control.content ?? EMPTY_CONTENT),
      show: control.show,
      onAccept: control.onConfirm,
    }),
    [control]
  );

  return { showWarnings, showSummary, dialog };
}
